import { useState } from 'react';
import { Download, Eraser, Eye, Trash2 } from 'lucide-react';
import { useLang } from '../../LangContext';
import { statusColor } from '../../utils/statusColor';
import { statusLabel } from '../../utils/statusLabel';
import { formatDate } from '../../utils/dateFormat';
import { useSubmissionFilters } from './submissions/useSubmissionFilters';
import { useSubmissionFilesDownload } from './submissions/useSubmissionFilesDownload';
import SubmissionFilterBar from './submissions/SubmissionFilterBar';
import SubmissionDetailModal from './submissions/SubmissionDetailModal';
import SubmissionExportButton from './submissions/SubmissionExportButton';

export default function SubmissionPage({ submissions, statuses, forms, loadSubmission, changeStatus, clearSubmissionFiles, deleteSubmission }) {
  const { locale: t, lang } = useLang();
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const { downloadingId, downloadFiles } = useSubmissionFilesDownload();

  const {
    filters,
    setFilters,
    filtersOpen,
    setFiltersOpen,
    formOptions,
    userOptions,
    filteredSubmissions,
    activeFilterCount,
    toggleMulti,
    resetFilters,
  } = useSubmissionFilters(submissions, forms, statuses);

  async function openSubmission(id) {
    try {
      const detail = await loadSubmission(id);
      if (detail) setSelectedSubmission(detail);
    } catch {
      // loadSubmission kendi alert'ini gösterir
    }
  }

  // Liste satırından yanlış gönderilen formu tamamen sil (modal'ı açmadan)
  async function handleListDelete(submissionId) {
    if (!window.confirm(t.submissions.confirmDelete)) return;
    await deleteSubmission(submissionId);
  }

  // Liste satırından dosyaları temizle (modal'ı açmadan)
  async function handleListClearFiles(submissionId) {
    if (!window.confirm(t.submissions.confirmClear)) return;
    try {
      await clearSubmissionFiles(submissionId);
    } catch {
      // clearSubmissionFiles kendi alert'ini gösterir
    }
  }

  return (
    <section className="section page-panel sp-page">
      <div className="section-header sp-header">
        <div>
          <h2>{t.submissions.title}</h2>
          <p className="muted">{t.submissions.subtitle}</p>
        </div>
        <div className="sp-header-actions">
          <span className="metric-pill">{t.submissions.records(filteredSubmissions.length, submissions.length)}</span>
          <SubmissionExportButton items={filteredSubmissions} />
        </div>
      </div>

      <SubmissionFilterBar
        filters={filters}
        setFilters={setFilters}
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        formOptions={formOptions}
        userOptions={userOptions}
        statuses={statuses}
        activeFilterCount={activeFilterCount}
        toggleMulti={toggleMulti}
        resetFilters={resetFilters}
      />

      <SubmissionListView
        items={filteredSubmissions}
        onOpen={openSubmission}
        onClear={handleListClearFiles}
        onDelete={handleListDelete}
        onDownload={downloadFiles}
        downloadingId={downloadingId}
        t={t.submissions}
        lang={lang}
      />

      {selectedSubmission && (
        <SubmissionDetailModal
          submission={selectedSubmission}
          statuses={statuses}
          onClose={() => setSelectedSubmission(null)}
          onChangeStatus={changeStatus}
          onClearFiles={clearSubmissionFiles}
          onDelete={deleteSubmission}
          onUpdated={setSelectedSubmission}
          lang={lang}
        />
      )}
    </section>
  );
}

// ----- Liste görünümü -----
function SubmissionListView({ items, onOpen, onClear, onDelete, onDownload, downloadingId, t, lang }) {
  const { locale } = useLang();
  if (items.length === 0) {
    return <div className="empty-state">{t.noResults}</div>;
  }
  return (
    <div className="sp-table-wrap">
      <table className="table sp-table">
        <thead>
          <tr>
            <th>{t.colForm}</th>
            <th>{t.colUser}</th>
            <th>{t.colStatus}</th>
            <th>{t.colSubmitted}</th>
            <th>{t.colUpdated}</th>
            <th className="sp-table-actions-th">{t.colActions}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const color = statusColor(item.statusCode);
            return (
              <tr key={item.id}>
                <td>{item.formName}</td>
                <td>{item.userName}</td>
                <td>
                  <span
                    className="sp-status-pill"
                    style={{ background: color.bg, borderColor: color.border, color: color.text }}
                  >
                    <span className="sp-chip-dot" style={{ background: color.text }} />
                    {statusLabel(locale, item.statusCode, item.status)}
                  </span>
                </td>
                <td>{formatDate(item.submittedAt, lang)}</td>
                <td>{item.updatedAt ? formatDate(item.updatedAt, lang) : '-'}</td>
                <td>
                  <div className="sp-row-actions">
                    <button
                      type="button"
                      className="sp-row-btn"
                      onClick={() => onDownload(item.id)}
                      disabled={downloadingId !== null}
                      title={t.download}
                    >
                      <Download size={14} />
                      <span>{downloadingId === item.id ? t.downloading : t.download}</span>
                    </button>
                    <button
                      type="button"
                      className="sp-row-btn"
                      onClick={() => onClear(item.id)}
                      title={t.clearFiles}
                    >
                      <Eraser size={14} />
                      <span>{t.clearFiles}</span>
                    </button>
                    <button
                      type="button"
                      className="sp-row-btn sp-row-btn-danger"
                      onClick={() => onDelete(item.id)}
                      title={t.deleteSubmission}
                    >
                      <Trash2 size={14} />
                      <span>{t.delete}</span>
                    </button>
                    <button
                      type="button"
                      className="sp-row-btn sp-row-btn-primary"
                      onClick={() => onOpen(item.id)}
                      title={t.inspect}
                    >
                      <Eye size={14} />
                      <span>{t.inspect}</span>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
